import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';

/**
 * /schools/join — Student sign in / join.
 *
 * Three modes — supports both kid-self-join AND teacher-created accounts:
 *  • first_time   → /api/schools/join (new self-join, picks own display name + PIN)
 *  • returning    → /api/schools/student-login with {display_name, pin}
 *  • username     → /api/schools/student-login with {student_username, pin}
 *                   for students the teacher provisioned with a unique handle.
 */
export default function StudentJoin() {
    const { logout } = useAuth();
    const [params] = useSearchParams();
    const [mode, setMode] = useState('first_time');
    const [form, setForm] = useState({ join_code: '', display_name: '', student_username: '', pin: '' });
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const code = (params.get('code') || '').toUpperCase().slice(0, 6);
        if (code) setForm((f) => ({ ...f, join_code: code }));
    }, [params]);

    const submit = async (e) => {
        e.preventDefault(); setErr(null); setBusy(true);
        try {
            const join_code = form.join_code.toUpperCase();
            let url, payload;
            if (mode === 'first_time') {
                url = '/schools/join';
                payload = { join_code, display_name: form.display_name, pin: form.pin };
            } else if (mode === 'returning') {
                url = '/schools/student-login';
                payload = { join_code, display_name: form.display_name, pin: form.pin };
            } else {
                url = '/schools/student-login';
                payload = { join_code, student_username: form.student_username, pin: form.pin };
            }
            const { data } = await api.post(url, payload);
            logout();
            localStorage.setItem('lq_token', data.token);
            window.location.href = '/dashboard';
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Something went wrong');
        } finally { setBusy(false); }
    };

    const MODE_TABS = [
        { slug: 'first_time', label: 'First time' },
        { slug: 'returning', label: 'Coming back' },
        { slug: 'username', label: 'I have a username' },
    ];

    return (
        <main className="max-w-md mx-auto px-6 py-16" data-testid="page-student-join">
            <div className="flex items-center gap-2 text-lq-sky-600 font-semibold text-xs uppercase tracking-wider">
                <GraduationCap size={14}/> Students
            </div>
            <h1 className="mt-1 font-display text-4xl font-semibold text-lq-ink-black">Join your class.</h1>
            <p className="mt-2 text-sm text-lq-ink-500">Get the join code from your teacher. No email needed.</p>

            <div className="mt-6 inline-flex bg-lq-sky-100 p-1 rounded-full flex-wrap" role="radiogroup">
                {MODE_TABS.map((m) => (
                    <button
                        key={m.slug}
                        type="button"
                        onClick={() => setMode(m.slug)}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold ${mode === m.slug ? 'bg-white shadow' : 'text-lq-sky-600'}`}
                        data-testid={`student-mode-${m.slug}`}
                    >
                        {m.label}
                    </button>
                ))}
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4">
                <label className="block">
                    <span className="text-sm font-medium">Join code</span>
                    <input required value={form.join_code} onChange={(e) => setForm({ ...form, join_code: e.target.value.toUpperCase().slice(0, 6) })} placeholder="6 letters/numbers" className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2 font-mono tracking-widest uppercase" data-testid="student-join-code"/>
                </label>
                {mode === 'username' ? (
                    <label className="block">
                        <span className="text-sm font-medium">Your username</span>
                        <span className="block text-xs text-lq-ink-500">Your teacher gave you this (for example, <code>aiyana-y6</code>).</span>
                        <input required value={form.student_username} onChange={(e) => setForm({ ...form, student_username: e.target.value.replace(/[^A-Za-z0-9._-]/g, '') })} minLength={3} maxLength={40} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2 font-mono" data-testid="student-username"/>
                    </label>
                ) : (
                    <label className="block">
                        <span className="text-sm font-medium">{mode === 'first_time' ? 'Choose your first name' : 'Your first name'}</span>
                        <input required value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="student-name"/>
                    </label>
                )}
                <label className="block">
                    <span className="text-sm font-medium">PIN {mode === 'first_time' && <span className="text-xs text-lq-ink-500">(pick 4 to 10 digits)</span>}</span>
                    <input required value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} type="password" minLength={4} maxLength={10} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="student-pin"/>
                </label>
                {err && <div className="text-sm text-lq-error" role="alert">{err}</div>}
                <button disabled={busy} className="w-full py-3 bg-lq-sky-600 text-white font-semibold rounded-full hover:bg-lq-sky-600/90 disabled:opacity-60" data-testid="student-submit">
                    {busy ? 'Working...' : mode === 'first_time' ? 'Join class' : 'Sign in'}
                </button>
            </form>
            <p className="mt-4 text-xs text-lq-ink-500 text-center">Parent or teacher? <Link to="/login" className="text-lq-purple-700 underline">Sign in here</Link>.</p>
        </main>
    );
}
